import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    deepStringFind,
    humanizeBytes,
    inject,
    Modal,
    ModalHandle,
    NothingHere,
    SubTopBar,
    Title,
    toArray,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsFolder } from '@react-icons/all-files/bs/BsFolder';
import { BsLayoutTextSidebarReverse } from '@react-icons/all-files/bs/BsLayoutTextSidebarReverse';
import { Input } from 'reactstrap';
import { AccountStore } from '../Account/AccountStore';
import { AccountsStore } from '../Accounts/AccountsStore';
import { AuditStore } from '../Audit/AuditStore';
import { AppStore } from '../Core/AppStore';
import { DocumentsStore } from '../Documents/DocumentsStore';
import { ManageStore } from '../Manage/ManageStore';
import { DocumentItem } from './DocumentItem';
import { BsGrid3X3GapFill } from '@react-icons/all-files/bs/BsGrid3X3GapFill';

@autoinject
@observer
export class DocumentsView extends React.Component {
    public constructor(props: any) {
        super(props);

        this.searchValue = this.appStore.storage.documentsSearch;
    }

    @bind
    @action
    public async componentDidMount() {
        await this.auditStore.loadAcceptedDocumentsTermsAndConditionsAsync(this.termsAndConditionsTitle);
        if (!this.auditStore.acceptedDocumentsTermsAndConditions) {
            this.confirmHandle.open();

            if (await this.confirmHandle.confirm()) {
                await this.acceptTerms();
            }
        }

        if (this.auditStore.acceptedDocumentsTermsAndConditions) {
            this.loadDocuments();
        }

        this.searchValue = this.appStore.storage.documentsSearch;
        this.appStore.storage.documentsSearch = '';
    }

    public render() {
        return (
            <div className="container-fluid py-3">
                <Title>Documents</Title>

                <SubTopBar>
                    <Input
                        type="text"
                        placeholder="Search Documents..."
                        value={this.searchValue}
                        onChange={this.onSearchInputChange}
                        className="mw-450"
                    />

                    <div className="px-2">
                        <a onClick={this.toggleView} className={this.listClass} id="ListView">
                            <BsLayoutTextSidebarReverse />
                        </a>

                        <a onClick={this.toggleView} className={this.cardClass} id="CardView">
                            <BsGrid3X3GapFill />
                        </a>
                    </div>
                </SubTopBar>

                <div className="row">
                    {this.auditStore.acceptedDocumentsTermsAndConditions ? (
                        <NothingHere
                            empty={!this.documents.length}
                            loading={this.loading}
                            icon={<BsFolder />}
                            label="No Documents Available"
                            loadingMessage="Loading Documents..."
                        />
                    ) : (
                        <NothingHere
                            empty
                            icon={<BsFolder />}
                            loading={this.loading}
                            label="Accept the Terms & Conditions to view documents."
                        />
                    )}

                    {this.documents &&
                        !this.loading &&
                        !this.listView &&
                        this.documents.map(d => (
                            <div className="col-md-4 my-3" key={d.fileName}>
                                <DocumentItem document={d} />
                            </div>
                        ))}

                    {this.documents &&
                        !this.loading &&
                        this.listView &&
                        this.documents.map(d => (
                            <div className="col-md-12 my-2" key={d.fileName}>
                                <a href={d.url}>
                                    {d.fileName} - {humanizeBytes(d.fileSize)}
                                </a>
                            </div>
                        ))}

                    <div className="col-12 text-center">
                        <a className="btn btn-link" onClick={this.confirmHandle.open}>
                            View Terms and Conditions
                        </a>
                    </div>
                </div>

                <Modal
                    title={this.termsAndConditionsTitle}
                    modalHandle={this.confirmHandle}
                    btnClass="w-50"
                    footerClass="d-flex justify-content-center"
                >
                    <div>
                        These documents, and any attachments thereto, regardless of form or medium, are intended only
                        for use by the addressee(s) and may contain legally privileged and/or confidential, copyrighted,
                        trademarked, patented or otherwise restricted information viewable by the intended recipient
                        only. If you are not the intended recipient of these documents (or the person responsible for
                        delivering this document to the intended recipient), you are hereby notified that any
                        dissemination, distribution, printing or copying of this document, and any attachment thereto,
                        is strictly prohibited and violation of this condition may infringe upon copyright, trademark,
                        patent, or other laws protecting proprietary and, or, intellectual property. In no event shall
                        these documents be delivered to anyone other than the intended recipient or original sender and
                        violation may be considered a breach of law fully punishable by various domestic and
                        international courts.
                        <br />
                        <br />I acknowledge that downloadable documents on the SmartVu® Advanced Monitoring Portal
                        contain confidential or proprietary information of Manufacturing Resources International and
                        will comply with the request to not reproduce, distribute, use, or disclose either in whole, or
                        in part, except as specifically authorized by Manufacturing Resources International.
                    </div>
                </Modal>
            </div>
        );
    }

    @bind
    @action
    private async loadDocuments() {
        this.loading = true;
        try {
            await this.accountsStore.loadAsync();
            const accounts = toArray(this.accountsStore.accounts);

            const promises = accounts.map(async a => {
                try {
                    await this.documentsStore.loadDocumentsForAccountAsync(a.accountNumber);
                } catch (e: any) {
                    console.error(e);
                }
            });

            await Promise.all(promises);
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    private async acceptTerms() {
        await this.auditStore.acceptDocumentsTermsAndConditionsAsync(this.termsAndConditionsTitle);
    }

    @bind
    @action
    private toggleView() {
        this.listView = !this.listView;
    }

    @computed
    private get cardClass() {
        if (!this.listView) {
            return 'btn btn-primary toggle-card active disabled';
        } else {
            return 'btn btn-primary toggle-card';
        }
    }

    @computed
    private get listClass() {
        if (this.listView) {
            return 'btn btn-primary toggle-list active disabled';
        } else {
            return 'btn btn-primary toggle-list';
        }
    }

    @bind
    @action
    private async onSearchInputChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.searchValue = target.value;
    }

    @computed
    private get documents() {
        let documents = toArray(this.documentsStore.documents);

        if (this.searchValue) documents = documents.filter(d => deepStringFind(d, this.searchValue));

        return documents;
    }

    private confirmHandle = new ModalHandle();

    @observable
    private listView = true;
    @observable
    private searchValue: string = '';
    @observable
    private loading = false;
    @inject
    private documentsStore!: DocumentsStore;
    @inject
    private auditStore!: AuditStore;
    @inject
    private manageStore!: ManageStore;
    @inject
    private appStore!: AppStore;
    @inject
    private accountStore!: AccountStore;
    @inject
    private accountsStore!: AccountsStore;
    private termsAndConditionsTitle = 'Documents Terms and Conditions Rev. 2';
}
